<template>
    <modal-controller-base contentClass="light-light-background purple-purple-color">
        <template #header>
            <custom-header back-button :title="$t(edit ? 'titleRoute.editProduct' : 'titleRoute.addProduct')">
                <template #start>
                    <ion-button
                        @click="modalController.dismiss()"
                        class="purple-light-color"
                    >
                        <ion-icon :icon="chevronBack"/>
                        {{ $t('button.back') }}
                    </ion-button>
                </template>
            </custom-header>
        </template>

        <ion-grid class="max-content-width-tablet">
            <ion-row>
                <ion-col class="fs-smaller purple-purple-color">
                    {{ $t(`placeholder.name`) }}
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="center-x">
                    <input-simple
                        input-class="grey4-grey4-background"
                        :placeholder="`${$t('placeholder.name')}*`"
                        v-model="newProduct.name"
                        :validate-option="{ name: 'name', label: $t('label.name') }"
                    />
                </ion-col>
            </ion-row>

            <template v-if="!edit">
                <ion-row>
                    <ion-col class="fs-smaller purple-purple-color">
                        {{ $t(`placeholder.quantity`) }}
                    </ion-col>
                </ion-row>

                <ion-row>
                    <ion-col class="center-x">
                        <input-simple
                            input-class="grey4-grey4-background"
                            :placeholder="`${$t('placeholder.quantity')}*`"
                            :type="INPUT_TYPE.NUMBER"
                            :inputmode="INPUT_MODE.NUMERIC"
                            v-model.number="newProduct.quantity"
                            :validate-option="{ name: 'quantity', label: $t('label.quantity') }"
                        />
                    </ion-col>
                </ion-row>

                <ion-row>
                    <ion-col class="fs-smaller purple-purple-color">
                        {{ $t(`label.targetStock`) }}
                    </ion-col>
                </ion-row>

                <ion-row>
                    <ion-col class="center-x">
                        <input-simple
                            input-class="grey4-grey4-background"
                            :placeholder="`${$t('label.targetStock')}`"
                            :type="INPUT_TYPE.NUMBER"
                            :inputmode="INPUT_MODE.NUMERIC"
                            v-model.number="newProduct.targetStock"
                            :validate-option="{ name: 'targetStock', label: $t('label.targetStock') }"
                        />
                    </ion-col>
                </ion-row>
            </template>

            <ion-row>
                <ion-col class="fs-smaller purple-purple-color">
                    {{ $t(`placeholder.packaging`) }}
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="center-x">
                    <input-simple
                        input-class="grey4-grey4-background"
                        :placeholder="$t('placeholder.packaging')"
                        v-model="newProduct.packaging"
                        :validate-option="{ name: 'packaging', label: $t('label.packaging') }"
                    />
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="fs-smaller purple-purple-color">
                    {{ $t(`placeholder.eancode`) }}
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="center-x">
                    <input-simple
                        input-class="grey4-grey4-background"
                        :placeholder="$t('placeholder.eancode')"
                        v-model="newProduct.eancode"
                        :validate-option="{ name: 'eancode', label: $t('label.eancode') }"
                    />
                </ion-col>
            </ion-row>

            <ion-row v-if="!props.listId">
                <ion-col class="fs-smaller purple-purple-color">
                    {{ $t(`label.list`) }}
                </ion-col>
            </ion-row>

            <ion-row v-if="!props.listId">
                <ion-col class="center-x">
                    <select-simple
                        v-model="listId"
                        :options="listStore.supplierLists(supplierId)"
                        value-key="id"
                        text-key="name"
                        :validate-option="{ name: 'listId', label: $t('label.list') }"
                    />
                </ion-col>
            </ion-row>

            <ion-row class="mt-3">
                <ion-col class="center-x">
                    <button-fix
                        :disabled="!meta.valid"
                        @click="addProduct"
                    >
                        {{ $t(edit ? 'button.edit' : 'button.add') }}
                    </button-fix>
                </ion-col>
            </ion-row>
        </ion-grid>
    </modal-controller-base>
</template>

<script setup lang="ts">
import { IonButton, IonCol, IonGrid, IonIcon, IonRow, modalController } from "@ionic/vue";
import { reactive, ref } from "vue";
import { route } from "@/router";
import { useForm } from "vee-validate";
import { t } from "@/plugins/vue-i18n";

import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import InputSimple from "@/components/input/InputSimple.vue";
import ButtonFix from "@/components/button/ButtonFix.vue";
import SelectSimple from "@/components/selects/SelectSimple.vue";

import { useListStore } from "@/stores/lists";
import { useSupplierStore } from "@/stores/suppliers";

import { replaceToast } from "@/functions/baseToast";

import { INPUT_MODE, INPUT_TYPE } from "@/types/enum/ionicEnum";
import { CustomProduct } from "@/types/supplier";

import { chevronBack } from "ionicons/icons";
import { isApp } from "@/functions/enviroment";
import { GetListItemsParams } from "@/types/list";
import { SORT_METHOD } from "@/types/enum/propEnum";
import { CreateProductProps } from "@/types/props";

const props = defineProps<CreateProductProps>()

const listStore = useListStore()
const supplierStore = useSupplierStore()

const listId = ref<string>(props.listId)
const locationId = route.value.params.locationId as string

const newProduct = reactive<CustomProduct>(props.product ? {
    name: props.product?.name,
    eancode: props.product?.eanCode,
    packaging: props.product?.packaging
} : {
    name: '',
    quantity: 0
})

const { meta, validate } = useForm({
    validationSchema: {
        name: 'required',
        targetStock: '',
        quantity: props.edit ? 'numeric' : 'required|numeric',
        listId: !props.listId ? 'required' : undefined
    }
})

const addProduct = async (): Promise<void> => {
    if (props.edit) {
        if (!props.listItemId) return
        await listStore.editCustomProduct({
            locationId,
            listId: listId.value,
            listItemId: props.listItemId,
            body: newProduct
        })
        window.dispatchEvent(new Event('reload-list'))
        while ((await modalController.getTop())) {
            await modalController.dismiss()
        }
        return
    }
    if (!(await validate()).valid) return

    const response = await listStore.addCustomProduct(locationId, listId.value, newProduct)

    if (!response) return

    // supplierStore.itemsLoaded = false
    await supplierStore.getCatalog({
        locationId: locationId,
        supplierId: props.supplierId,
    })
    if (!isApp() && props.listId) await supplierStore.getCatalog({
        locationId: locationId,
        supplierId: props.supplierId,
        includeList: listId.value
    })
    window.dispatchEvent(new Event('force-reload-catalog'))
    // supplierStore.isLoaded.catalog = true
    replaceToast({
        message: t('messages.dashboard.catalog.createdCustomProduct'),
        id: 'added-product'
    })

    if (!isApp() && props.listId) {
        listStore.isLoaded.listItems = false
        await listStore.getListItems(new GetListItemsParams({
            locationId,
            listId: props.listId,
            searchKey: listStore.searchQuery !== '' ? listStore.searchQuery : undefined,
            category: listStore.listFilter.category !== '' ? listStore.listFilter.category : undefined,
            tags: listStore.activeFiltersIds.length !== 0 ? listStore.activeFiltersIds.join(',') : undefined,
            withQuantity: listStore.listFilter.withQuantity,
            // sortField: (listStore.sortDesc) ? 'name' : undefined,
            sortDesc: (listStore.listFilter.sortDirection === SORT_METHOD.ALPH_DESC)
        }))
        listStore.isLoaded.listItems = true
        window.dispatchEvent(new Event('force-reload-catalog'))
    }

    modalController.dismiss(undefined, 'add')
}
</script>
